/**
 * PartBBeneficialOwner - TASK-371 (extracted from FormAForm)
 * Part B: Beneficial Owner Declaration — owner type, identity, PEP status,
 * and additional owners list.
 */

import React from 'react';
import { InheritedBadge } from './InheritedBadge';
import { BeneficialOwnerSection } from './BeneficialOwnerSection';
import type { BeneficialOwnerEntry } from './BeneficialOwnerSection';
import type { FormAFormData } from './FormAForm';

type BeneficialOwnerData = FormAFormData['beneficial_owner'];

interface PartBBeneficialOwnerProps {
  data: BeneficialOwnerData;
  additionalOwners: BeneficialOwnerEntry[];
  onChange: (field: string, value: any) => void;
  onAddOwner: () => void;
  onRemoveOwner: (index: number) => void;
  onAdditionalOwnerChange: (index: number, field: keyof BeneficialOwnerEntry, value: string | number) => void;
  errors: Record<string, string>;
  isInherited: boolean;
}

import { fieldError } from '../../../utils/formHelpers';

export const PartBBeneficialOwner: React.FC<PartBBeneficialOwnerProps> = ({
  data,
  additionalOwners,
  onChange,
  onAddOwner,
  onRemoveOwner,
  onAdditionalOwnerChange,
  errors,
  isInherited,
}) => (
  <div className="px-4 pb-4 space-y-4">
    {/* Owner type toggle */}
    <div className="flex items-center gap-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="owner_type"
          value="self"
          checked={data.owner_type === 'self'}
          onChange={() => onChange('owner_type', 'self')}
          className="w-4 h-4 text-blue-600"
        />
        <span className="text-sm text-gray-700">Account holder is the beneficial owner</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="owner_type"
          value="third_party"
          checked={data.owner_type === 'third_party'}
          onChange={() => onChange('owner_type', 'third_party')}
          className="w-4 h-4 text-blue-600"
        />
        <span className="text-sm text-gray-700">Third-party beneficial owner</span>
      </label>
    </div>

    {/* Primary BO fields */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Name *
          {isInherited && <InheritedBadge />}
        </label>
        <input
          type="text"
          value={data.name || ''}
          onChange={(e) => onChange('name', e.target.value)}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors['beneficial_owner.name'] ? 'border-red-300' : 'border-gray-200'}`}
          placeholder="Full legal name"
        />
        {fieldError(errors, 'beneficial_owner.name')}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Date of Birth
          {isInherited && <InheritedBadge />}
        </label>
        <input
          type="date"
          value={data.date_of_birth || ''}
          onChange={(e) => onChange('date_of_birth', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Nationality
          {isInherited && <InheritedBadge />}
        </label>
        <input
          type="text"
          value={data.nationality || ''}
          onChange={(e) => onChange('nationality', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g. Swiss"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Domicile
          {isInherited && <InheritedBadge />}
        </label>
        <input
          type="text"
          value={data.domicile || ''}
          onChange={(e) => onChange('domicile', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Country of domicile"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Ownership %
        </label>
        <input
          type="number"
          min="0"
          max="100"
          value={data.percentage}
          onChange={(e) =>
            onChange('percentage', parseFloat(e.target.value) || 0)
          }
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>

    {/* PEP section */}
    <div className="space-y-3 pt-2 border-t border-gray-100">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_pep"
          checked={data.is_pep}
          onChange={(e) => onChange('is_pep', e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded"
        />
        <label htmlFor="is_pep" className="text-sm text-gray-700">
          Beneficial owner is a Politically Exposed Person (PEP)
        </label>
      </div>
      {data.is_pep && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              PEP Position
            </label>
            <input
              type="text"
              value={data.pep_position || ''}
              onChange={(e) => onChange('pep_position', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Member of Parliament"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              PEP Country
            </label>
            <input
              type="text"
              value={data.pep_country || ''}
              onChange={(e) => onChange('pep_country', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Country of PEP status"
            />
          </div>
        </div>
      )}
    </div>

    {/* Additional owners */}
    {data.owner_type === 'third_party' && (
      <div className="pt-3 border-t border-gray-100">
        <BeneficialOwnerSection
          owners={additionalOwners}
          onAdd={onAddOwner}
          onRemove={onRemoveOwner}
          onChange={onAdditionalOwnerChange}
        />
      </div>
    )}
  </div>
);

export default PartBBeneficialOwner;
